import { Dispatch, SetStateAction } from "react";
import { Stack } from "@mui/material";
import { Badge, DirectionsCar, EvStation, LocalParking } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { SettingsAccordion } from "./CompanyCard-Setting-Accordion.partial";
import { BookingType } from "../../../Booking-Form/typings/booking-inputs";
import { CheckinAndMaxAdvancedBooking, MetaData } from "../../../Login/typings/login.types";
import { CompanyCardDescriptionMaximumAdvancedBooking } from "../partial/CompanyCard-Description-MaximumAdvancedBooking.partial";
import { MaximumAdvancedBookingSettings } from "../partial/CompanyCard-MaximumAdvanceBookingSettings.partial";
import {
  AdvancedBookingPerTypeRowIcon,
  AdvancedPerTypeAccordion
} from "./CompanyCard-Setting-AdvancedPerTypeAccordion.partial";
import { AdvancedBookingSettingsGroup, AdvancedBookingSettingsSetters } from "../CompanyCard.types";
import { ReactComponent as AvatarIcon } from "../../../Booking-Form/assets/avatar.svg";
import { ReactComponent as ConferenceIcon } from "../../../Booking-Form/assets/conference.svg";
import { generateRowContents } from "../functions/generateAdvancedPerTypeAccordionRowContents.function";
import {
  ELECTRIC_CHARGING_PLACE_TYPE_ID,
  PARKING_PLACE_TYPE_ID,
  VEHICLE_PLACE_TYPE_ID,
  WORKPLACE_PLACE_TYPE_ID,
  CONFERENCE_ZONE_TYPE_ID
} from "../../../Booking-Form/typings/place-and-zone-type-ids.constant";
import { getBookingTypeEnabled } from "./company-card-booking-type-enabling.config";

type Props = {
  newSetting: AdvancedBookingSettingsGroup;
  setNewSetting: AdvancedBookingSettingsSetters;
  newValues: MetaData;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
};

export const AdvancedBookingAccordion = ({
  newSetting,
  setNewSetting,
  newValues,
  setNewValues
}: Props) => {
  const { t } = useTranslation();
  const workplaceBookingEnabled = getBookingTypeEnabled(newValues, "workplaceBookingEnabled");
  const parkingplaceBookingEnabled = getBookingTypeEnabled(newValues, "parkingplaceBookingEnabled");
  const electricchargingplaceBookingEnabled = getBookingTypeEnabled(
    newValues,
    "electricchargingplaceBookingEnabled"
  );
  const conferencezoneBookingEnabled = getBookingTypeEnabled(
    newValues,
    "conferencezoneBookingEnabled"
  );
  const vehicleBookingEnabled = getBookingTypeEnabled(newValues, "vehicleBookingEnabled");
  const visitorBookingEnabled = getBookingTypeEnabled(newValues, "visitorBookingEnabled");

  const {
    userInformation: { placeTypes, zoneTypes, visitorManagementEnabled }
  } = useSelector((state: RootState) => state.login);

  const rowContentsPerType = (setting: CheckinAndMaxAdvancedBooking) => (
    <span>{generateRowContents(setting, t)}</span>
  );

  return (
    <SettingsAccordion
      title={t("Maximum advanced booking")}
      description={<CompanyCardDescriptionMaximumAdvancedBooking />}
    >
      <Stack spacing={2}>
        {/* maxAdvanced booking time for WORKPLACE */}
        {workplaceBookingEnabled && placeTypes.some(t => t.id === WORKPLACE_PLACE_TYPE_ID) && (
          <AdvancedPerTypeAccordion
            title={t("Workplace")}
            contents={rowContentsPerType(newSetting.wpSetting)}
            typeIcon={
              <AdvancedBookingPerTypeRowIcon icon={<AvatarIcon width={"18px"} height={"18px"} />} />
            }
          >
            <MaximumAdvancedBookingSettings
              sliderLabelType={"Workplace"}
              bookingType={BookingType.WORKPLACE}
              setting={newSetting.wpSetting}
              setNewSetting={setNewSetting.setWpSetting}
              newValues={newValues}
              setNewValues={setNewValues}
              data-testid={"maximumAdvancedWorkplaceBooking"}
            />
          </AdvancedPerTypeAccordion>
        )}

        {/* maxAdvanced booking time for PARKINGPLACE */}
        {parkingplaceBookingEnabled && placeTypes.some(t => t.id === PARKING_PLACE_TYPE_ID) && (
          <AdvancedPerTypeAccordion
            title={t("Parking")}
            contents={rowContentsPerType(newSetting.parkPlSetting)}
            typeIcon={
              <AdvancedBookingPerTypeRowIcon
                icon={<LocalParking width={"18px"} height={"18px"} />}
              />
            }
          >
            <MaximumAdvancedBookingSettings
              sliderLabelType={"Parking"}
              bookingType={BookingType.PARKINGPLACE}
              setting={newSetting.parkPlSetting}
              setNewSetting={setNewSetting.setParkPlSetting}
              newValues={newValues}
              setNewValues={setNewValues}
              data-testid={"maximumAdvancedParkingplaceBooking"}
            />
          </AdvancedPerTypeAccordion>
        )}

        {/* maxAdvanced booking time for ELECTRICCHARGINGSTATIONPLACE */}
        {electricchargingplaceBookingEnabled &&
          placeTypes.some(t => t.id === ELECTRIC_CHARGING_PLACE_TYPE_ID) && (
            <AdvancedPerTypeAccordion
              title={t("Electric Charging")}
              contents={rowContentsPerType(newSetting.eChargePlSetting)}
              typeIcon={
                <AdvancedBookingPerTypeRowIcon
                  icon={<EvStation width={"18px"} height={"18px"} />}
                />
              }
            >
              <MaximumAdvancedBookingSettings
                sliderLabelType={"Electric Charging"}
                bookingType={BookingType.ELECTRICCHARGINGSTATIONPLACE}
                setting={newSetting.eChargePlSetting}
                setNewSetting={setNewSetting.setEChargePlSetting}
                newValues={newValues}
                setNewValues={setNewValues}
                data-testid={"maximumAdvancedElectricchargingplaceBooking"}
              />
            </AdvancedPerTypeAccordion>
          )}

        {/* maxAdvanced booking time for VEHICLE */}
        {vehicleBookingEnabled && placeTypes.some(t => t.id === VEHICLE_PLACE_TYPE_ID) && (
          <AdvancedPerTypeAccordion
            title={t("_bookingTypeVehicle")}
            contents={rowContentsPerType(newSetting.vehicleSetting)}
            typeIcon={
              <AdvancedBookingPerTypeRowIcon
                icon={<DirectionsCar width={"18px"} height={"18px"} />}
              />
            }
          >
            <MaximumAdvancedBookingSettings
              sliderLabelType={"_bookingTypeVehicle"}
              bookingType={BookingType.VEHICLE}
              setting={newSetting.vehicleSetting}
              setNewSetting={setNewSetting.setVehicleSetting}
              newValues={newValues}
              setNewValues={setNewValues}
              data-testid={"maximumAdvancedVehicleBooking"}
            />
          </AdvancedPerTypeAccordion>
        )}

        {/* maxAdvanced booking time for CONFERENCEZONE */}
        {conferencezoneBookingEnabled && zoneTypes.some(t => t.id === CONFERENCE_ZONE_TYPE_ID) && (
          <AdvancedPerTypeAccordion
            title={t("_bookTypeMeetingRoom")}
            contents={rowContentsPerType(newSetting.confZnSetting)}
            typeIcon={
              <AdvancedBookingPerTypeRowIcon
                icon={<ConferenceIcon width={"18px"} height={"18px"} />}
              />
            }
          >
            <MaximumAdvancedBookingSettings
              sliderLabelType={"_bookTypeMeetingRoom"}
              bookingType={BookingType.CONFERENCEZONE}
              setting={newSetting.confZnSetting}
              setNewSetting={setNewSetting.setConfZnSetting}
              newValues={newValues}
              setNewValues={setNewValues}
              data-testid={"maximumAdvancedConferencezoneBooking"}
            />
          </AdvancedPerTypeAccordion>
        )}

        {/* maxAdvanced booking time for VISITOR */}
        {visitorBookingEnabled && visitorManagementEnabled && (
          <AdvancedPerTypeAccordion
            title={t("_bookingTypeVisitor")}
            contents={rowContentsPerType(newSetting.visitorSetting)}
            typeIcon={
              <AdvancedBookingPerTypeRowIcon icon={<Badge width={"18px"} height={"18px"} />} />
            }
          >
            <MaximumAdvancedBookingSettings
              sliderLabelType={"_bookingTypeVisitor"}
              bookingType={BookingType.VISITOR}
              setting={newSetting.visitorSetting}
              setNewSetting={setNewSetting.setVisitorSetting}
              newValues={newValues}
              setNewValues={setNewValues}
              data-testid={"maximumAdvancedVisitorBooking"}
            />
          </AdvancedPerTypeAccordion>
        )}
      </Stack>
    </SettingsAccordion>
  );
};
